<script setup lang="ts">
/**
 * "Forgot password" form. FE-014 — redesign primitives; the request and the
 * navigation back to sign-in are unchanged.
 */
import { computed, ref } from "vue";
import { useAuthModal } from "@/features/auth/store";
import { useAuthStore } from "@/features/auth/store";
import { useI18n } from "vue-i18n";
import { FairButton } from "@/ui/primitives/FairButton";
import { FairInputText } from "@/ui/primitives/FairInputText";

const { t } = useI18n();
const email = ref("");
const emailError = ref("");
const loadingState = ref(false);

const disableSubmit = computed(() => {
  return email.value === "" || !!emailError.value.length;
});

function signInHandler() {
  useAuthModal().showAuthModal("signIn");
}

const validateEmail = () => {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailPattern.test(email.value)) {
    emailError.value = t("signup.email_error");
  } else {
    emailError.value = "";
  }
};

async function forgotPasswordHandler() {
  if (email.value && !emailError.value) {
    loadingState.value = true;
    try {
      await useAuthStore().forgotPassword(email.value);
    } finally {
      loadingState.value = false;
    }
  }
}
</script>

<template>
  <form
    class="fair-auth-form"
    @submit.prevent="forgotPasswordHandler"
  >
    <div class="fair-auth-status">
      <h2 class="fair-auth-status__title">
        {{ t("reset_password.forgot_title") }}
      </h2>
      <p class="fair-auth-status__text">
        {{ t("reset_password.instruction") }}
      </p>
    </div>

    <div class="fair-auth-form__field">
      <FairInputText
        v-model="email"
        input-id="auth-reset-email"
        :placeholder="t('signup.email_label')"
        :aria-label="t('signup.email_label')"
        :invalid="!!emailError"
        @input="validateEmail"
        @blur="validateEmail"
      />
      <p
        v-if="emailError"
        class="fair-auth-form__error"
      >
        {{ emailError }}
      </p>
    </div>

    <FairButton
      class="fair-auth-form__submit"
      type="submit"
      variant="primary"
      size="m"
      :rounded="false"
      :label="t('reset_password.submit_button')"
      :disabled="disableSubmit || loadingState"
    />

    <p class="fair-auth-switch">
      {{ t("reset_password.back_to") }}
      <button
        type="button"
        class="fair-auth-form__link"
        @click="signInHandler"
      >
        {{ t("reset_password.sign_in") }}
      </button>
    </p>
  </form>
</template>
